import { Buffer } from 'buffer';

type ThriftCallback = (error: unknown, result?: unknown) => void;

type ThriftClient = {
  _reqs: Record<number, ThriftCallback | undefined>;
  [member: string]: unknown;
};

type BufferedTransport = object;

type BufferedTransportConstructor = {
  receiver(callback: (transport: BufferedTransport) => void): (data: Buffer) => void;
};

type Protocol = {
  readMessageBegin(): { fname: string; mtype: number; rseqid: number };
};

type ProtocolConstructor = new (transport: BufferedTransport) => Protocol;

type Result<T> =
  | { ok: true; value: T }
  | { ok: false; cause: unknown };

export type XhrThriftConnection = {
  client?: ThriftClient;
  headers: Record<string, string>;
  options?: { timeout?: number };
  protocol: ProtocolConstructor;
  transport: BufferedTransportConstructor;
  url?: string;
  getXmlHttpRequestObject(): XMLHttpRequest;
  listenerCount?(event: string): number;
  emit?(event: string, error: Error): boolean;
  write(buffer: unknown, sequenceId?: number): void;
};

type Envelope = {
  method: string;
  messageType: number;
  sequenceId: number;
};

const JSON_PROTOCOL_VERSION = 1;
const CALL = 1;
const REPLY = 2;
const EXCEPTION = 3;

function requestError(method: string, detail: string, cause?: unknown): Error {
  const error = new Error(`Thrift RPC ${method} ${detail}`);
  if (cause !== undefined) {
    Object.defineProperty(error, 'cause', { configurable: true, value: cause });
  }
  return error;
}

function endpointLabel(url: string | undefined): string {
  if (!url) return 'the configured endpoint';
  return url.split(/[?#]/, 1)[0] || 'the configured endpoint';
}

function toText(buffer: unknown): string {
  if (typeof buffer === 'string') return buffer;
  if (buffer instanceof Uint8Array) return Buffer.from(buffer).toString('utf8');
  if (buffer instanceof ArrayBuffer) return Buffer.from(new Uint8Array(buffer)).toString('utf8');
  throw new Error('response body is not text');
}

function parseEnvelope(buffer: unknown): Result<Envelope> {
  let decoded: unknown;
  try {
    decoded = JSON.parse(toText(buffer));
  } catch (cause) {
    return { ok: false, cause };
  }
  if (!Array.isArray(decoded) || decoded.length !== 5) {
    return { ok: false, cause: new Error('message envelope is invalid') };
  }
  const [version, method, messageType, sequenceId] = decoded;
  if (
    version !== JSON_PROTOCOL_VERSION
    || typeof method !== 'string'
    || typeof messageType !== 'number'
    || !Number.isInteger(sequenceId)
  ) {
    return { ok: false, cause: new Error('message envelope is invalid') };
  }
  return { ok: true, value: { method, messageType, sequenceId } };
}

function emitTransportError(connection: XhrThriftConnection, error: Error): void {
  if (!connection.emit || (connection.listenerCount && connection.listenerCount('error') === 0)) return;
  connection.emit('error', error);
}

/**
 * Replaces only the broken browser XHR write path from apache-thrift. Generated
 * clients and the mutable connection.headers API remain unchanged.
 */
export function installReliableXhrTransport<T extends XhrThriftConnection>(connection: T): T {
  connection.write = (buffer: unknown): void => {
    const parsedRequest = parseEnvelope(buffer);
    if (!parsedRequest.ok) {
      throw requestError('unknown', 'cannot send a malformed request', parsedRequest.cause);
    }
    const request = parsedRequest.value;
    if (request.messageType !== CALL) {
      throw requestError(request.method, 'cannot send an invalid request message type');
    }

    const client = connection.client;
    const callback = client?._reqs[request.sequenceId];
    if (!client || !callback) {
      throw requestError(request.method, 'has no pending request callback');
    }

    let settled = false;
    const settle = (error: unknown, result?: unknown, notify = false): void => {
      if (settled) return;
      settled = true;
      delete client._reqs[request.sequenceId];
      delete client._reqs[-request.sequenceId];
      callback(error, result);
      if (notify && error instanceof Error) emitTransportError(connection, error);
    };
    const reject = (detail: string, cause?: unknown): void => {
      settle(requestError(request.method, detail, cause), undefined, true);
    };

    const decodeResponse = (responseText: string): Result<void> => {
      try {
        const parsedResponse = parseEnvelope(responseText);
        if (!parsedResponse.ok) {
          return {
            ok: false,
            cause: requestError(request.method, 'received a malformed response', parsedResponse.cause),
          };
        }
        const response = parsedResponse.value;
        if (response.method !== request.method) {
          return { ok: false, cause: requestError(request.method, 'received a response for a different method') };
        }
        if (response.sequenceId !== request.sequenceId) {
          return { ok: false, cause: requestError(request.method, 'received a response with a different sequence id') };
        }
        if (response.messageType !== REPLY && response.messageType !== EXCEPTION) {
          return { ok: false, cause: requestError(request.method, 'received an invalid response message type') };
        }

        const receive = client[`recv_${request.method}`];
        if (typeof receive !== 'function') {
          return { ok: false, cause: requestError(request.method, 'has no generated response decoder') };
        }
        connection.transport.receiver((transport) => {
          const protocol = new connection.protocol(transport);
          const header = protocol.readMessageBegin();
          if (
            header.fname !== request.method
            || header.rseqid !== request.sequenceId
            || (header.mtype !== REPLY && header.mtype !== EXCEPTION)
          ) {
            throw requestError(request.method, 'received a malformed response header');
          }
          const decodeSequenceId = -request.sequenceId;
          client._reqs[decodeSequenceId] = (error, result) => settle(error, result);
          try {
            receive.call(client, protocol, header.mtype, decodeSequenceId);
            if (!settled) throw requestError(request.method, 'received a response that did not settle its request');
          } finally {
            delete client._reqs[decodeSequenceId];
          }
        })(Buffer.from(responseText));
        return { ok: true, value: undefined };
      } catch (cause) {
        if (settled) throw cause;
        return { ok: false, cause };
      }
    };

    const startRequest = (): Result<void> => {
      try {
        const xhr = connection.getXmlHttpRequestObject();
        xhr.overrideMimeType('application/json');
        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) return;
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(xhr.status === 0
              ? `failed to reach ${endpointLabel(connection.url)}`
              : `failed with HTTP ${xhr.status} at ${endpointLabel(connection.url)}`);
            return;
          }
          const decoded = decodeResponse(xhr.responseText);
          if (!decoded.ok) reject('could not decode the response', decoded.cause);
        };
        xhr.onerror = (event) => reject(`failed to reach ${endpointLabel(connection.url)}`, event);
        xhr.ontimeout = (event) => reject(`timed out contacting ${endpointLabel(connection.url)}`, event);
        xhr.onabort = (event) => reject(`was aborted while contacting ${endpointLabel(connection.url)}`, event);

        xhr.open('POST', connection.url ?? '', true);
        if (connection.options?.timeout) xhr.timeout = connection.options.timeout;
        const headers = { ...connection.headers };
        for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
        xhr.send(buffer as XMLHttpRequestBodyInit);
        return { ok: true, value: undefined };
      } catch (cause) {
        if (settled) throw cause;
        return { ok: false, cause };
      }
    };

    const started = startRequest();
    if (!started.ok) reject(`could not start at ${endpointLabel(connection.url)}`, started.cause);
  };
  return connection;
}
